/**
 * Dropdown — the row-level and header action menu (Edit, Duplicate, Delete…).
 *
 * Deliberately not portalled: the panel is absolutely positioned inside a wrapping
 * div, which keeps it glued to its trigger while a table scrolls and lets one
 * `useDismissable` call cover both the trigger and the menu for outside clicks.
 * Focus roves through `role="menuitem"` buttons (all `tabIndex={-1}`, one focused
 * at a time) exactly as the WAI-ARIA menu button pattern prescribes.
 */
import {
  cloneElement,
  forwardRef,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/cn';
import { useDismissable } from './Modal';

export interface DropdownItem {
  key: string;
  label: ReactNode;
  /** Decorative leading icon; rendered inside an `aria-hidden` wrapper. */
  icon?: ReactNode;
  onSelect: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  /** Draw a divider above this item to start a new group. */
  separatorBefore?: boolean;
}

/** Props injected into the caller's trigger element. */
interface TriggerProps {
  onClick?: (event: ReactMouseEvent<HTMLElement>) => void;
  onKeyDown?: (event: ReactKeyboardEvent<HTMLElement>) => void;
  'aria-haspopup'?: 'menu';
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'data-state'?: 'open' | 'closed';
}

export interface DropdownProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Usually a `<Button>`; cloned so it receives the menu button ARIA wiring. */
  trigger: ReactNode;
  items: DropdownItem[];
  /** Which edge of the trigger the menu lines up with. */
  align?: 'start' | 'end';
  /** Accessible name for the menu itself. */
  label?: string;
  menuClassName?: string;
}

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(function Dropdown(
  { trigger, items, align = 'end', label = 'Actions', menuClassName, className, ...props },
  forwardedRef,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const menuId = useId();

  const close = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  // The wrapper holds the trigger too, so a click on it never counts as "outside".
  useDismissable({
    isOpen,
    onClose: close,
    ref: containerRef,
    trapFocus: false,
    lockScroll: false,
    closeOnOutsideClick: true,
    autoFocus: false,
  });

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    itemRefs.current[activeIndex]?.focus();
  }, [isOpen, activeIndex]);

  const enabledIndexes = items.reduce<number[]>((indexes, item, index) => {
    if (!item.disabled) indexes.push(index);
    return indexes;
  }, []);

  const openAt = (position: 'first' | 'last') => {
    if (enabledIndexes.length === 0) return;
    setIsOpen(true);
    setActiveIndex(position === 'first' ? enabledIndexes[0] : enabledIndexes[enabledIndexes.length - 1]);
  };

  const moveActive = (direction: 1 | -1) => {
    if (enabledIndexes.length === 0) return;
    const current = enabledIndexes.indexOf(activeIndex);
    const next =
      current === -1
        ? direction === 1
          ? 0
          : enabledIndexes.length - 1
        : (current + direction + enabledIndexes.length) % enabledIndexes.length;
    setActiveIndex(enabledIndexes[next]);
  };

  const handleTriggerClick = () => {
    if (isOpen) close();
    else setIsOpen(true);
  };

  const handleTriggerKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    // Enter and Space are handled here rather than left to the button's native
    // click so that opening from the keyboard lands focus on the first item,
    // as the menu button pattern requires. preventDefault stops the click that
    // would otherwise follow and toggle the menu straight back shut.
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (isOpen) moveActive(1);
      else openAt('first');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (isOpen) moveActive(-1);
      else openAt('last');
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (isOpen) close();
      else openAt('first');
    }
  };

  const handleMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        if (enabledIndexes.length > 0) setActiveIndex(enabledIndexes[0]);
        break;
      case 'End':
        event.preventDefault();
        if (enabledIndexes.length > 0) setActiveIndex(enabledIndexes[enabledIndexes.length - 1]);
        break;
      case 'Tab':
        // Tabbing away dismisses the menu but keeps the browser's focus move.
        close();
        break;
      default:
        break;
    }
  };

  const handleSelect = (item: DropdownItem) => {
    if (item.disabled) return;
    close();
    item.onSelect();
  };

  const triggerProps: TriggerProps = {
    onClick: handleTriggerClick,
    onKeyDown: handleTriggerKeyDown,
    'aria-haspopup': 'menu',
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? menuId : undefined,
    'data-state': isOpen ? 'open' : 'closed',
  };

  /** Clone an element trigger so the caller keeps their own handlers and styling. */
  function renderTrigger(): ReactNode {
    if (!isValidElement(trigger)) {
      return (
        <button
          type="button"
          {...triggerProps}
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-surface px-3 text-sm font-medium text-content shadow-xs transition-colors hover:bg-surface-sunken"
        >
          {trigger}
        </button>
      );
    }

    const element = trigger as ReactElement<TriggerProps>;
    return cloneElement(element, {
      ...triggerProps,
      onClick: (event: ReactMouseEvent<HTMLElement>) => {
        element.props.onClick?.(event);
        if (!event.defaultPrevented) handleTriggerClick();
      },
      onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
        element.props.onKeyDown?.(event);
        if (!event.defaultPrevented) handleTriggerKeyDown(event);
      },
    });
  }

  return (
    <div
      ref={(node) => {
        containerRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      }}
      className={cn('relative inline-block text-left', className)}
      {...props}
    >
      {renderTrigger()}

      {isOpen ? (
        <div
          id={menuId}
          role="menu"
          aria-orientation="vertical"
          aria-label={label}
          onKeyDown={handleMenuKeyDown}
          className={cn(
            'absolute z-40 mt-2 min-w-[12rem] rounded-xl border border-border bg-surface-raised p-1 shadow-popover animate-scale-in',
            align === 'end' ? 'right-0 origin-top-right' : 'left-0 origin-top-left',
            menuClassName,
          )}
        >
          {items.map((item, index) => (
            /* A Fragment keeps every menuitem a direct child of role="menu". */
            <Fragment key={item.key}>
              {item.separatorBefore && index > 0 ? (
                <div role="separator" className="my-1 h-px bg-border" />
              ) : null}
              <button
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={item.disabled}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => !item.disabled && setActiveIndex(index)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                  'disabled:pointer-events-none disabled:opacity-50',
                  item.tone === 'danger'
                    ? 'text-danger hover:bg-danger-soft focus:bg-danger-soft'
                    : 'text-content hover:bg-surface-sunken focus:bg-surface-sunken',
                )}
              >
                {item.icon ? (
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden="true">
                    {item.icon}
                  </span>
                ) : null}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </button>
            </Fragment>
          ))}
        </div>
      ) : null}
    </div>
  );
});
